import { Typography, useMediaQuery, useTheme } from '@mui/material';
import './Home.scss';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ImageSlider from './Carousel/Carousel';
import Marquee from 'react-fast-marquee';
import NDCNews from './NDCNews';
import NewsletterBanner from './NewsletterBanner/NewsletterBanner';
import SuccessStoriesCarousel from './SuccessStories/SuccessStoriesCarousel';
import ExploreOptions from './ExploreOptions/ExploreOptions';
import ConnectionOptionsCarousel from './ConnectionOptionsCarousel/ConnectionOptionsCarousel';
import FeatureGrid from './FeatureGrid/FeatureGrid';

// const parser = new DOMParser();

interface Content {
  infoBoxes: {
    [key: string]: {
      link: string;
      image: string;
      header: string;
      content: string[];
    };
  };
  explore: {
    links: {
      [key: string]: {
        link: string;
        image: string;
        text: string;
      };
    };
    infoBoxes: {
      [key: string]: {
        image: string;
        header: string;
        subheader: string;
        links: string[];
      };
    };
  };
  news: {
    releases: {
      [key: string]: {
        header: string;
        link: string;
        text: string;
      };
    };
    image: string;
    header: string;
  };
  keyFeatures: {
    header: string;
    bodyText: string;
    table: string;
  };
  mainHeader: string;
  mainSubHead: string;
}

const Home = ({ logoHeight }: { logoHeight: number }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const scroller = t('scroller_scroller_image', {
    returnObjects: true,
    defaultValue: []
  });

  const logoAnnounce = t('ndc-program.home.partners.logo', {
    returnObjects: true
  });

  const sellerList = Array.isArray(scroller)
    ? scroller
        .map((item: any) => item?.alt?.replace('logo', ''))
        .filter(Boolean)
    : [];

  const scrollerTitle: any = t('scroller_title', {
    returnObjects: true
  });

  const removehTags = (str?: string): string => {
    if (!str) {
      return '';
    }
    return str.replace(/^<h2>/, '').replace(/<\/h2>$/, '');
  };

  const scrollerArray = Object.values(scroller);

  const content = t('ndc-program.home', {
    returnObjects: true,
    defaultValue: []
  }) as Content;

  const getFullImageLink = (image: string) => {
    const env = process.env['REACT_APP_ENV_LOADED'];
    let url = `https://content-ndc-portal.aircanada.com`; //default to prod
    if (env === 'DEV' || env === 'INT' || env === 'CRT') {
      url = `https://content-ndc-portal-int.aircanada.com`;
    } else if (env === 'BAT' || env === 'PREPROD') {
      url = `https://content-ndc-portal-bat.aircanada.com`;
    }
    return url + image;
  };
  // const handleAnchorClick = useHandleAnchorClick();
  // const isTablet = useMediaQuery('(min-width:768px) and (max-width:1200px)');
  const isNotDekstop = useMediaQuery(theme.breakpoints.down(768));

  return (
    content && (
      <div style={{ marginTop: `${logoHeight - 60}px` }}>
        <ImageSlider logoHeight={logoHeight} />

        <main className={`homeBody ${isMobile && 'homeMobile'}`}>
          <section className="vertical-spacing featureGridBg">
            <div className="new-center-width ">
              <div className="keyFeatures" aria-labelledby="keyFeaturesID">
                <h1
                  className="mainHeader"
                  id="keyFeaturesID"
                >{t('featureGrid.title')}</h1>
                <div
                  className="mainSubHeader "
                >{t('featureGrid.supportingText')}</div>
                <FeatureGrid />
              </div>
            </div>
          </section>

          <div className="exploreOptionsContainer">
            <div
              style={{ backgroundColor: '#F1F7FB' }}
              className="new-center-width"
            >
              <ExploreOptions></ExploreOptions>
            </div>
          </div>

          <section
            aria-roledescription="carousel"
            className="partnersSection"
            aria-label={t(
              'ndc-program.home.partners.accessibility.containerHead'
            )}
          >
            <div className="new-center-width">
              <h2 id="partnersHeader" className="mainHeader" tabIndex={0}>
                {removehTags(scrollerTitle.value)}
              </h2>

              <div id="partnersMarqueeContainer" tabIndex={0}>
                <div
                  style={{
                    border: 0,
                    clip: 'rect(0 0 0 0)',
                    height: 1,
                    margin: -1,
                    overflow: 'hidden',
                    padding: 0,
                    position: 'absolute',
                    width: 1,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <ul>
                    {sellerList.map((name, idx) => (
                      <li key={idx}>{`${logoAnnounce} ${name}`}</li>
                    ))}
                  </ul>
                </div>
                <Marquee
                  gradient={false}
                  speed={30}
                  pauseOnHover
                  direction="left"
                  className="partnersMarquee"
                >
                  <ul>
                    {scrollerArray.map(
                      (partner, index) =>
                        partner.id % 2 === 1 && (
                          <>
                            <img
                              key={index}
                              src={getFullImageLink(partner.url)}
                              alt={`Logo of ${partner.name}`}
                              className="partnerLogo"
                              aria-hidden={true}
                            />
                          </>
                        )
                    )}
                  </ul>
                </Marquee>

                <Marquee
                  gradient={false}
                  speed={32}
                  pauseOnHover
                  delay={2}
                  direction="left"
                >
                  <ul>
                    {scrollerArray.map(
                      (partner, index) =>
                        partner.id % 2 === 0 && (
                          <>
                            <img
                              key={index}
                              src={getFullImageLink(partner.url)}
                              alt={`Logo of ${partner.name}`}
                              className="partnerLogo"
                            />
                          </>
                        )
                    )}
                  </ul>
                </Marquee>
              </div>
            </div>
          </section>
          <section className="vertical-spacing">
            <div className="new-center-width">
              <ConnectionOptionsCarousel></ConnectionOptionsCarousel>
            </div>
          </section>

          <section
            className="vertical-spacing"
            aria-labelledby="quotes-heading"
          >
            <div className="new-center-width">
              <SuccessStoriesCarousel
                data={t('quotes_array', {
                  returnObjects: true,
                  defaultValue: []
                })}
              />
            </div>
          </section>

          <div
            className="new-center-width vertical-spacing"
            id="newsletter-banner"
          >
            <NewsletterBanner />
          </div>

          <section
            style={{
              backgroundColor: '#F9F9F9'
            }}
            className="vertical-spacing"
            // role="region"
            aria-live="polite"
          >
            <div className="new-center-width">
              <Typography
                id="newBoxSection"
                component="h2"
                sx={{
                  fontWeight: '700 !important',
                  fontSize: '36px',
                  lineHeight: '48px',
                  color: '#000000',
                  pt: 5
                }}
              >
                {t('ndc_news_var.title')}
              </Typography>
              <div aria-labelledby="newBoxSection">
                <NDCNews
                  data={t('ndc_news', {
                    returnObjects: true,
                    defaultValue: []
                  })}
                />
              </div>
            </div>
          </section>
        </main>
      </div>
    )
  );
};

export default Home;
